'use client';

import { LinearProgress, SelectChangeEvent } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { ApolloQueryResult, OperationVariables, useMutation } from '@parkchirp/api-client';
import React, { ChangeEvent, FC, SyntheticEvent, useCallback, useState } from 'react';
import { SAVE_BANK_ACCOUNT } from '../../queries';
import type { SetShowSnackbar } from '../../types';

type BankAccountData = {
	firstName: string;
	lastName: string;
	businessName: string;
	accountNumber: string;
	routingNumber: string;
	accountType: string;
};
interface BankAccountProps {
	cognitoSub: string;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	refetchPaymentMethods?: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	fullWidth?: boolean;
	BusinessOrIndividualComponent?: React.ComponentType<{ isBusiness: boolean; onChange: (_, val) => void }> | null;
	AddNewAccountButton?: any;
	paymentProviderId: number;
}

type BankAccountInputChange = SelectChangeEvent<string> | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

export const BankAccountForm: FC<BankAccountProps> = ({
	cognitoSub,
	setShowSnackbar,
	tenantSubscriptionId,
	refetchPaymentMethods,
	fullWidth = false,
	BusinessOrIndividualComponent = null,
	AddNewAccountButton = null,
	paymentProviderId,
}) => {
	const [isBusiness, setIsBusiness] = useState<boolean>(false);
	const [bankAccountData, setBankAccountData] = useState<BankAccountData>({
		firstName: '',
		lastName: '',
		businessName: '',
		accountNumber: '',
		routingNumber: '',
		accountType: '',
	});

	const [savePaymentToken, { loading: saveAccountLoading }] = useMutation(SAVE_BANK_ACCOUNT, {
		onCompleted: () => {
			setShowSnackbar({
				message:
					'Bank account added. In order to verify this account, you may see a micro charge that will be refunded.',
				severity: 'success',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setBankAccountData({
				firstName: '',
				lastName: '',
				businessName: '',
				accountNumber: '',
				routingNumber: '',
				accountType: '',
			});

			if (refetchPaymentMethods) {
				refetchPaymentMethods();
			}
		},
	});

	const handleAccountType = useCallback(
		(value?: any) => {
			if (value) {
				setIsBusiness(!isBusiness);
			}
		},
		[isBusiness],
	);

	const handleBankAccountInputChange = (event: BankAccountInputChange) => {
		const { name, value } = event.target;

		if (name === 'accountNumber' && value.length > 17) {
			setBankAccountData((prev) => ({ ...prev, [name]: value.slice(0, 17) }));
		} else if (name === 'routingNumber' && value.length > 9) {
			setBankAccountData((prev) => ({ ...prev, [name]: value.slice(0, 9) }));
		} else {
			setBankAccountData((prev) => ({ ...prev, [name]: value }));
		}
	};

	const { firstName, lastName, businessName, accountNumber, routingNumber, accountType } = bankAccountData;

	const handleValidation = useCallback(() => {
		let requiredFields = [
			{ label: 'Account #', value: accountNumber },
			{ label: 'Routing #', value: routingNumber },
			{ label: 'Account Type', value: accountType },
		];

		if (isBusiness) {
			requiredFields.push({ label: 'Business Name', value: businessName });
		} else {
			requiredFields = requiredFields.concat([
				{ label: 'First Name', value: firstName },
				{ label: 'Last Name', value: lastName },
			]);
		}
		const missingFields = requiredFields.filter(({ value }) => ['', null, undefined].includes(value));
		if (missingFields.length) {
			const missingAsString = missingFields?.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to add due to missing field(s): ${missingAsString}`,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [firstName, lastName, businessName, isBusiness, accountNumber, routingNumber, accountType, setShowSnackbar]);

	const handleBankAccountSubmit = useCallback(
		async (event: SyntheticEvent) => {
			event.preventDefault();
			if (handleValidation()) {
				try {
					await savePaymentToken({
						variables: {
							tenantSubscriptionId,
							gpi_bank_account: {
								bank_account_number: accountNumber,
								bank_routing_number: routingNumber,
								bank_account_type: accountType,
								name: isBusiness
									? { business_name: businessName }
									: {
											first_name: firstName,
											last_name: lastName,
										},
							},
							cognitoSub,
							paymentProviderId,
						},
					});
				} catch (error: any) {
					setShowSnackbar({
						message: error.message,
						severity: 'error',
						autoHideDuration: 3000,
						onClose: () => {
							setShowSnackbar(null);
						},
					});
				}
			}
		},
		[
			handleValidation,
			savePaymentToken,
			setShowSnackbar,
			cognitoSub,
			isBusiness,
			tenantSubscriptionId,
			accountNumber,
			accountType,
			routingNumber,
			businessName,
			firstName,
			lastName,
			paymentProviderId,
		],
	);

	const isSaveDisabled = routingNumber.length !== 9 || accountNumber.length < 8 || accountNumber.length > 17;

	return (
		<Box className='pca__gpi-bank-account'>
			<Grid container spacing={2} sx={{ width: fullWidth ? '100%' : '70%' }}>
				<Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: -2 }}>
					{BusinessOrIndividualComponent ? (
						<BusinessOrIndividualComponent
							onChange={(_, val) => handleAccountType(val)}
							isBusiness={isBusiness}
						/>
					) : (
						<Button style={{ padding: '3px 9px' }} size='small' onClick={(val) => handleAccountType(val)}>
							{isBusiness ? 'This is an individual account' : 'This is a business account'}
						</Button>
					)}
				</Grid>
				{isBusiness ? (
					<Grid item xs={12}>
						<TextField
							required
							fullWidth
							label='Business Name'
							name='businessName'
							InputLabelProps={{ shrink: true }}
							value={businessName}
							onChange={handleBankAccountInputChange}
							inputProps={{ 'data-cy': 'bank-account-business-name-input' }}
						/>
					</Grid>
				) : (
					<>
						<Grid item xs={6}>
							<TextField
								required
								fullWidth
								label='First Name'
								name='firstName'
								InputLabelProps={{ shrink: true }}
								value={firstName}
								onChange={handleBankAccountInputChange}
								inputProps={{ 'data-cy': 'bank-account-first-name-input' }}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								required
								fullWidth
								label='Last Name'
								name='lastName'
								InputLabelProps={{ shrink: true }}
								value={lastName}
								onChange={handleBankAccountInputChange}
								inputProps={{ 'data-cy': 'bank-account-last-name-input' }}
							/>
						</Grid>
					</>
				)}
				<Grid item xs={4}>
					<TextField
						required
						fullWidth
						label={`Account # (${accountNumber?.length || 0})`}
						name='accountNumber'
						InputLabelProps={{ shrink: true }}
						type='number'
						value={accountNumber}
						onChange={handleBankAccountInputChange}
						inputProps={{ 'data-cy': 'bank-account-account-number-input' }}
					/>
					<div style={{ fontSize: '12px', color: 'grey', marginTop: '2px', marginLeft: '5px' }}>
						Account # must be 8-17 characters in length
					</div>
				</Grid>
				<Grid item xs={4}>
					<TextField
						required
						fullWidth
						label={`Routing # (${routingNumber?.length || 0})`}
						name='routingNumber'
						InputLabelProps={{ shrink: true }}
						type='number'
						value={routingNumber}
						onChange={handleBankAccountInputChange}
						inputProps={{ 'data-cy': 'bank-account-routing-number-input' }}
					/>
					<div style={{ fontSize: '12px', color: 'grey', marginTop: '2px', marginLeft: '5px' }}>
						Routing # must be 9 characters in length
					</div>
				</Grid>
				<Grid item xs={4}>
					<FormControl style={{ width: '100%' }}>
						<InputLabel required id='type-label'>
							Account Type
						</InputLabel>
						<Select
							required
							fullWidth
							variant='outlined'
							label='Account Type'
							labelId='type-label'
							id='type'
							name='accountType'
							value={accountType}
							onChange={handleBankAccountInputChange}
							data-cy='bank-account-account-type-select'
						>
							<MenuItem value='CHECKING'>Checking</MenuItem>
							<MenuItem value='SAVINGS'>Savings</MenuItem>
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Box marginTop={2}>
				{AddNewAccountButton ? (
					<AddNewAccountButton onClick={handleBankAccountSubmit} />
				) : (
					<Button
						variant='contained'
						size='small'
						className='add-gpi-bank-account'
						onClick={handleBankAccountSubmit}
						disabled={isSaveDisabled || saveAccountLoading}
						data-cy='bank-account-save-button'
					>
						Add New Account
					</Button>
				)}

				{saveAccountLoading && (
					<LinearProgress
						color='primary'
						data-cy='loading-indicator'
						style={{ maxWidth: '300px', marginTop: '5px' }}
					/>
				)}
			</Box>
		</Box>
	);
};
